import PartnerDocumentsPanel from '@/components/PartnerDocumentsPanel';
import PartnerExternUsersPanel from '@/components/PartnerExternUsersPanel';
import { StableTabsContent, StableTabsContentGroup } from '@/components/StableTabsContent';
import { Tabs } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '@/i18n/I18nContext';

interface PartnerDetailTabsProps {
    partnerId: number;
    children: ReactNode;
}

const tabValues = ['details', 'documents', 'users'] as const;
type PartnerDetailTab = typeof tabValues[number];

const resolveTab = (value: string | null | undefined): PartnerDetailTab =>
    tabValues.includes(value as PartnerDetailTab) ? value as PartnerDetailTab : 'details';

const PartnerDetailTabs = ({ partnerId, children }: PartnerDetailTabsProps) => {
    const { t } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'));

    const onTabChange = ({ value }: { value: string }) => {
        const nextTab = resolveTab(value);
        const nextSearchParams = new URLSearchParams(searchParams);

        if (nextTab === 'details') {
            nextSearchParams.delete('tab');
        } else {
            nextSearchParams.set('tab', nextTab);
        }

        setSearchParams(nextSearchParams, { replace: true });
    };

    return (
        <Tabs.Root
            value={activeTab}
            onValueChange={onTabChange}
        >
            <Tabs.List>
                <Tabs.Trigger value='details'>
                    {t('partners.details.tab')}
                </Tabs.Trigger>
                <Tabs.Trigger value='documents'>
                    {t('partners.details.documents')}
                </Tabs.Trigger>
                <Tabs.Trigger value='users'>
                    {t('partners.details.users')}
                </Tabs.Trigger>
            </Tabs.List>
            <StableTabsContentGroup>
                <StableTabsContent value='details' activeValue={activeTab}>
                    {children}
                </StableTabsContent>
                <StableTabsContent value='documents' activeValue={activeTab}>
                    <PartnerDocumentsPanel filters={{ partnerId }} />
                </StableTabsContent>
                <StableTabsContent value='users' activeValue={activeTab}>
                    <PartnerExternUsersPanel partnerId={partnerId} />
                </StableTabsContent>
            </StableTabsContentGroup>
        </Tabs.Root>
    );
};

export default PartnerDetailTabs;
